<template>
  <div class="doc-container font-roboto-light top-border">
    <div class="container">
      <div class="row flex-xl-nowrap g-0">
        <SidebarDocumentation/>
        <!-- ****************************** DOC MAIN BODY ********************************* -->
        <main class="col-xl-9 col-md-8 doc-main-body">

          <h2>Add a chatbot trained on your data (OpenAI)</h2>

          <h3>What is OpenAI Assistant?</h3>
          <p>
            <span class="font-roboto-medium">OpenAI Assistant</span> is an OpenAI service that gives you access to a variety of LLMs (large language models) that can generate text data. If you send a request to an LLM asking for help with general tasks (such as writing an email or answering common questions), it will usually be able to help.<br>
            However, these models don't have any knowledge about your custom data (e.g., details about your SaaS, its capabilities, etc.). To address this, we need to store relevant data and use it when making requests to LLMs. Your custom data is stored in a vector database.
          </p>

          <p>
            <span class="font-roboto-medium">OpenAI Assistant includes a serverless vector database</span> where you can store your custom data (such as information about your SaaS). When a user asks your chatbot a question, the backend of your SaaS retrieves the most relevant parts of that data and sends them to the LLM to generate a final response.
          </p>


          <p>If this sounds confusing, do not worry, SaaSConstruct does all this behind the scenes, you only need to do an initial configuration.</p>

          <h3>1. Set values for the global-dev-config.ts file</h3>
          <p>Now we need to configure the <span class="font-roboto-medium">global-dev-config.ts file</span> and <span class="font-roboto-medium">global-prod-config.ts file</span>, which you'll find in the <span class="font-roboto-medium">'cdk/global-config'</span> folder.
            <br> Change only this field:
          </p>
          <CodeContainer :code="globalConfig.code" :language="globalConfig.language"/>

          <h3 style="margin-top: 40px !important;">2. Create an account on OpenAI platform and get the API key</h3>
          <ul class="list-item-one">
            Go to the  <a href="https://platform.openai.com/" target="_blank" rel="noopener noreferrer" class="underline-link">OpenAI platform</a>, create a new account (if you don't have one) and get the API key.
          </ul>

          <h3 style="margin-top: 40px !important;">3. Create an OpenAI Assistant and get the ID of your assistant</h3>
          <ul class="list-item-one">
            <li>
              Go to the  <a href="https://platform.openai.com/playground/assistants?mode=assistant" target="_blank" rel="noopener noreferrer" class="underline-link">OpenAI Assistant</a>, and create a new assistant.
              <div class="documentation-image">
                <img
                    src="../../../../assets/images/documentation/ai/openai/create_open_ai_assistant.png"
                    alt=""
                    class="shapes screen-one"
                    style="width: 70%; height: 70%"
                />
              </div>
            </li>
            <li class="mt-25">
              Give system instructions to your Assistant. For example: "Provide concise guidance to my product: [here describe your product]". You can use a Helper button to generate system instructions for you. <br>
              After that choose the model (for example, gpt-4-turbo).
              <div class="documentation-image">
                <img
                    src="../../../../assets/images/documentation/ai/openai/system_instructions.png"
                    alt=""
                    class="shapes screen-one"
                    style="width: 60%; height: 60%"
                />
              </div>
            </li>
            <li class="mt-25">
              If you want an assistant to provide answers based on your custom data (for example, your documentation), please do the following: <br>

              - Enable File search. Then upload your custom data to the vector database. It can be, for example, documentation for your product.
                <div class="documentation-image mb-25">
                  <img
                      src="../../../../assets/images/documentation/ai/openai/file_search.png"
                      alt=""
                      class="shapes screen-one"
                      style="width: 60%; height: 60%"
                  />
                </div>
              - In the settings, adjust the setting "Max num results" to 10 (if you choose a GPT-4 model, or 5 for GPT-3 models).
              <div class="documentation-image mb-25">
                <img
                    src="../../../../assets/images/documentation/ai/openai/max_documents.png"
                    alt=""
                    class="shapes screen-one"
                    style="width: 60%; height: 60%"
                />
              </div>
              - Response format should be text, and temperature should be minimal (0.01).
              <div class="documentation-image mb-25">
                <img
                    src="../../../../assets/images/documentation/ai/openai/temperature.png"
                    alt=""
                    class="shapes screen-one"
                    style="width: 60%; height: 60%"
                />
              </div>
            </li>

            <li class="mt-25">
              Get the ID of your Assistant.
              <div class="documentation-image">
                <img
                    src="../../../../assets/images/documentation/ai/openai/assistant_id.png"
                    alt=""
                    class="shapes screen-one"
                    style="width: 60%; height: 60%"
                />
              </div>
            </li>

            <li class="mt-25">
              Optional: you can send test messages to your assistant to see if it works as expected.
              <div class="documentation-image">
                <img
                    src="../../../../assets/images/documentation/ai/openai/playground.png"
                    alt=""
                    class="shapes screen-one"
                    style="width: 60%; height: 60%"
                />
              </div>
            </li>
          </ul>

          <h3 style="margin-top: 40px !important;">4. Save OpenAI API key and Assistant ID to AWS Secrets Manager</h3>
          <ul class="list-item-one">
            <p>Go to AWS Secret Manager (dev and prod accounts) and update your secrets. Add the following:</p>
            <li>
              <span class="font-roboto-medium">open_ai_key</span> - OpenAI API key.
            </li>
            <li>
              <span class="font-roboto-medium">open_ai_assistant_id</span> - Assistant ID.
              <div class="documentation-image">
                <img
                    src="../../../../assets/images/documentation/ai/openai/ai_secret_manager.png"
                    alt=""
                    class="shapes screen-one"
                />
              </div>
            </li>
          </ul>


          <h3 style="margin-top: 40px !important;">5. Deploy changes</h3>

          <ul class="list-item-one">
            <li>
              Now just push the code to your GitHub repository:
            </li>
            <CodeContainer class="mt-15" :code="commitPush.code" :language="commitPush.language"/>
            <li class="mt-15">After the rollout has been completed, you can test your chatbot.
              <div class="documentation-image">
                <img
                    src="../../../../assets/images/documentation/ai/chatbot_gif.gif"
                    alt=""
                    class="shapes screen-one"
                    style="width: 100%; height: 100%"
                />
              </div>
            </li>
          </ul>
          <DocumentationPagination/>
        </main> <!-- /.doc-main-body -->
      </div>
    </div>
  </div>
</template>

<script>
import SidebarDocumentation from "@/components/service-documentation/commons/SidebarDocumentation.vue";
import DocumentationPagination from "@/components/service-documentation/commons/DocumentationPagination.vue";
import {ref} from "vue";
import CodeContainer from "@/components/service-documentation/commons/CodeContainer.vue";

export default {
  name: 'AddChatbotOpenAI',
  components: {
    CodeContainer,
    SidebarDocumentation,
    DocumentationPagination,
  },
  setup() {

    const globalConfig = ref({
      language: 'python',
      code:
          'chatbot: "OPENAI",'
    });

    const commitPush = ref({
      language: 'bash',
      code:
          'cd saasconstruct # navigate to the project folder\n' +
          'git add .\n'+
          'git commit -m "support OpenAI chatbot"\n'+
          'git push origin main # pushes code to the main branch (prod)\n'+
          'git push origin main:development # pushes current main branch to development (dev)\n'
    });

    return {
      globalConfig,
      commitPush
    };
  }
}
</script>
